import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import React from 'react'
import { Trans } from 'react-i18next'

import { foodGroupsQuery, ingredientsSearchQuery, toggleIngredientVisibility } from '@/api'
import NotificationBanner from '@/components/NotificationBanner/NotificationBanner'
import { Spinner } from '@/components/Spinner'
import { TabPanel } from '@/components/Tabs'
import FCDBIngredientsPanel from '@/features/ingredients/IngredientIndex/FCDBIngredientsPanel'
import { SearchIngredient } from '@/api/types'
import { useSharedPersistentState } from '@/hooks/useSharedPersistentState'
import { FCDB_INGREDIENTS_BANNER } from './ingredients'

export type UnitsFilter = 'WITH_UNITS' | 'WITHOUT_UNITS' | 'ALL'
export type VisibilityFilter = 'HIDDEN' | 'VISIBLE' | 'ALL'
export type IngredientGroupFilter = 'WITH_IG' | 'WITHOUT_IG' | 'ALL'

export type IngredientsSearchParams = {
  languaLID?: Array<string>
  search?: string
  page?: number
  pageSize?: number
  foodGroup?: Array<number>
  onlyUsedInRecipes?: boolean
  includeLanguaLSubtree?: boolean
  unitsFilter?: UnitsFilter
  visibilityFilter?: VisibilityFilter
  ingredientGroupFilter?: IngredientGroupFilter
  lang?: string
  searchLang?: string
}

const createIngredientsQuery = (queryParams: IngredientsSearchParams) =>
  ingredientsSearchQuery({ ...queryParams })

export const Route = createFileRoute('/ingredients/fcdb')({
  component: IngredientsComponent,
  pendingComponent() {
    return <Spinner />
  },
  validateSearch: (search: Record<string, unknown>): IngredientsSearchParams => {
    // validate and parse the search params into a typed state
    return {
      languaLID: search.languaLID as Array<string>,
      search: search.search as string,
      page: search.page ? parseInt(search.page as string, 10) : 0,
      foodGroup: search.foodGroup as Array<number>,
      onlyUsedInRecipes: search.onlyUsedInRecipes as boolean,
      includeLanguaLSubtree: (search.includeLanguaLSubtree as boolean) ?? true,
      unitsFilter: (search.unitsFilter as UnitsFilter) ?? 'ALL',
      visibilityFilter: (search.visibilityFilter as VisibilityFilter) ?? 'ALL',
      ingredientGroupFilter: (search.ingredientGroupFilter as IngredientGroupFilter) ?? 'ALL',
    }
  },
})

function IngredientsComponent() {
  const queryClient = useQueryClient()
  const searchParams = useSearch({ from: '/ingredients/fcdb' })
  const ingredientsQuery = createIngredientsQuery(searchParams)
  const { data: ingredients } = useQuery(ingredientsQuery)
  const { data: foodGroups } = useQuery(foodGroupsQuery)
  const [isFCDBIngBannerVisible, setIsFCDBIngBannerVisible] = useSharedPersistentState(
    FCDB_INGREDIENTS_BANNER,
    true,
  )

  const toggleVisibilityMutation = useMutation({
    mutationFn: ({ ingredientId, state }: { ingredientId: number; state: boolean }) =>
      toggleIngredientVisibility(ingredientId, state),
    onMutate: async ({ ingredientId, state }) => {
      await queryClient.cancelQueries({ queryKey: ['ingredients-search'] })

      queryClient.setQueriesData<{ data: SearchIngredient[] }>(ingredientsQuery, (oldData) => {
        if (oldData) {
          oldData.data = oldData.data.map((ingredient) => {
            if (ingredient.idIngredient === ingredientId) {
              return {
                ...ingredient,
                hideFromUser: state,
              }
            }
            return ingredient
          })

          return oldData
        }
      })
    },
  })

  const handleToggleVisibility = (ingredientId: number, state: boolean) => {
    toggleVisibilityMutation.mutate({ ingredientId, state })
  }

  return (
    <TabPanel id="fcdb">
      {isFCDBIngBannerVisible && (
        <NotificationBanner
          style={{ marginTop: '5px' }}
          onClose={() => setIsFCDBIngBannerVisible(false)}>
          <Trans i18nKey="ingredient.fcdbIngredientsBanner" />
        </NotificationBanner>
      )}
      <FCDBIngredientsPanel
        ingredients={ingredients ? ingredients.data : []}
        foodGroups={foodGroups || []}
        totalCount={parseInt(ingredients?.headers['x-result-count'])}
        handleToggleVisibility={handleToggleVisibility}
      />
    </TabPanel>
  )
}
